import type { VariableMeta } from '@tsagko/label-contracts'
import type { KeyboardEvent } from 'react'

export interface VariableFieldProps {
  name: string
  meta: VariableMeta
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  /** Forwarded only to the `<input>` control (text/number/date); ignored for select types. */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
}

function inputTypeFor(type: VariableMeta['type']): 'text' | 'number' | 'date' {
  if (type === 'number') return 'number'
  if (type === 'date') return 'date'
  return 'text'
}

const controlClass =
  'mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-950'

export function VariableField({
  name,
  meta,
  value,
  onChange,
  onBlur,
  onKeyDown,
}: VariableFieldProps) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {name}
        </span>
        <span className="font-mono text-[10px] text-stone-400">{meta.type}</span>
      </span>
      {meta.type === 'enum' ? (
        <select
          aria-label={name}
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          className={controlClass}
        >
          {(meta.enumOptions ?? []).map(o => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : meta.type === 'boolean' ? (
        <select
          aria-label={name}
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          className={controlClass}
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : (
        <input
          aria-label={name}
          type={inputTypeFor(meta.type)}
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className={controlClass}
        />
      )}
    </label>
  )
}
